// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, Kpi, AppFooter, useCurrentUser, apiErrorMessage, PhoneInput, PasswordInput } from "./common";
import { LocationPicker } from "../components/naver/LocationPicker";
import { useEscapeKey } from "../components/useEscapeKey";
import { Pager } from "../components/Pager";
import { RowMenu } from "../components/RowMenu";
import { fullTimestamp } from "../components/timeFormat";
import { usePermissionsFromSession } from "../components/permissions";

// 회원 관리 — 일반회원(어르신·보호자)의 탈퇴/재활성화.
// system_admin은 전 권역, region_admin은 자기 권역 회원만(백엔드 스코프가 강제).
// 관리자·운영자 계정은 /admins, 케어매니저는 /managers로 분리돼 있다.

const ROLE_LABEL = {
  citizen:  { label: "어르신",        color: "sky"   },
  guardian: { label: "보호자",        color: "green" },
};
const STATE_LABEL = {
  active:  { label: "활성", color: "green" },
  invited: { label: "초대", color: "amber" },
  paused:  { label: "정지", color: "rose"  },
  off:     { label: "탈퇴", color: "gray"  },
};

const PER_PAGE = 20;

// 탈퇴 시각을 절대 날짜로. null → "—".
function withdrawnLabel(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
          .replace(/\.\s?/g, "-").replace(/-$/, "");
}

async function authGateMessage(res) {
  if (res.status === 401) return "로그인이 필요합니다.";
  if (res.status === 403) {
    const j = await res.json().catch(() => ({}));
    return j.message || "회원 관리 권한이 필요합니다.";
  }
  return null;
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

// ── 회원 신규 등록(운영자 대리 등록) ─────────────────────────────────
//
// 역할(어르신 본인 / 보호자)에 따라 입력 항목이 갈린다 — /signup의 역할별 폼과
// 같은 필수 항목·같은 문구를 쓰되, 여기선 운영자가 대신 채운다.
// 서버(members#create)가 ConsumerRegistration으로 같은 규칙을 재검증하므로
// 이 폼의 검사는 "빨간 테두리로 먼저 알려주기" 용도다.

const HOUSEHOLDS = ["독거", "노인부부", "가족동거", "시설", "기타"];
const RELATIONS  = ["자녀", "배우자", "형제·자매", "친척", "기타"];

const CREATE_ROLES = [
  { v: "citizen",  label: "어르신 본인", sub: "본인이 직접 쓰는 계정", color: "sky"   },
  { v: "guardian", label: "보호자",     sub: "자녀·배우자 등 가족",   color: "green" },
];

function MemberCreateModal({ user, onClose, onDone }) {
  const isSystemAdmin = user?.role === "system_admin";
  const [role, setRole] = React.useState("citizen");
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "", password: "",
    age: "", address: "", household_type: "", health_status: "", region_id: "",
    latitude: null, longitude: null,
    elderly_name: "", elderly_phone: "",
    relation: "", connect_mode: "new", connect_code: "",
    guardian_name: "", guardian_phone: "",
  });
  const [opts, setOpts] = React.useState({ consent: false, care_request: true, send_invite: true });
  const [regions, setRegions] = React.useState([]);
  const [errs, setErrs] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);
  useEscapeKey(onClose);

  React.useEffect(() => {
    fetch("/api/v1/regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(rs => {
        const list = Array.isArray(rs) ? rs : (rs.regions || []);
        setRegions(list);
        // 권역 관리자는 자기 권역 고정 — 서버도 강제하지만 폼에도 채워 보여준다.
        if (!isSystemAdmin && user?.region_id) {
          setForm(f => ({ ...f, region_id: String(user.region_id) }));
        }
      })
      .catch(() => setRegions([]));
  }, [isSystemAdmin, user?.region_id]);

  const set = (k) => (v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrs(e => (e[k] ? { ...e, [k]: false } : e));
  };
  const eStyle = (k) => errs[k]
    ? { borderColor: 'var(--rose-500)', boxShadow: '0 0 0 2px rgba(201,89,79,0.15)' }
    : undefined;

  // 기존 어르신 연결(보호자 + connect_mode=existing)이면 어르신 정보 입력이 없다.
  const linkingExisting = role === "guardian" && form.connect_mode === "existing";
  const newElderly = !linkingExisting;

  // 역할별 필수 항목 — [필드키, 라벨]. 서버 검증(ConsumerRegistration)과 같은 집합.
  const requiredFields = () => {
    const f = [["name", "이름"], ["email", "이메일"]];
    if (role === "citizen") {
      f.push(["age", "나이"], ["household_type", "가구형태"], ["address", "주소"], ["region_id", "소속 권역"],
             ["guardian_name", "보호자 성함"], ["guardian_phone", "보호자 연락처"], ["relation", "보호자와의 관계"]);
    } else {
      f.push(["relation", "어르신과의 관계"]);
      if (linkingExisting) f.push(["connect_code", "연결코드"]);
      else f.push(["elderly_name", "어르신 성함"], ["age", "나이"], ["household_type", "가구형태"],
                  ["address", "주소"], ["region_id", "소속 권역"]);
    }
    return f;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    const missing = requiredFields().filter(([k]) => !String(form[k] ?? "").trim());
    if (missing.length) {
      setErrs(Object.fromEntries(missing.map(([k]) => [k, true])));
      setErr(`필수 항목을 입력해주세요: ${missing.map(([, l]) => l).join(", ")}`);
      return;
    }
    if (!opts.consent) { setErr("이용약관·개인정보·민감정보 처리 동의 확인이 필요합니다."); return; }
    setBusy(true); setErr(null);
    try {
      const body = {
        role,
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        password: form.password.trim() || undefined,
        relation: form.relation || undefined,
        consent_confirmed: true,
        send_invite: opts.send_invite,
        create_care_request: opts.care_request,
      };
      if (linkingExisting) {
        body.connect_mode = "existing";
        body.connect_code = form.connect_code.trim();
      } else {
        Object.assign(body, {
          connect_mode: "new",
          age: form.age, address: form.address.trim(),
          household_type: form.household_type,
          health_status: form.health_status.trim() || undefined,
          region_id: form.region_id,
          latitude: form.latitude ?? undefined, longitude: form.longitude ?? undefined,
        });
        if (role === "citizen") {
          Object.assign(body, {
            guardian_name: form.guardian_name.trim(),
            guardian_phone: form.guardian_phone.trim(),
          });
        } else {
          Object.assign(body, {
            elderly_name: form.elderly_name.trim(),
            elderly_phone: form.elderly_phone.trim() || undefined,
          });
        }
      }
      const r = await fetch("/api/v1/members", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) throw new Error(apiErrorMessage(data, r.status));
      onDone(data);
    } catch (e2) {
      setErr(apiErrorMessage(e2)); setBusy(false);
    }
  };

  const regionName = regions.find(r => String(r.id) === String(form.region_id))?.name;
  const regionCenter = (() => {
    const reg = regions.find(r => String(r.id) === String(form.region_id));
    return reg && reg.center_latitude != null && reg.center_longitude != null
      ? { lat: reg.center_latitude, lng: reg.center_longitude } : null;
  })();

  // 어르신 정보 입력 묶음 — citizen(본인) / guardian(신규 어르신 등록) 공용.
  const elderlyFields = (
    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
      {role === "guardian" && (<>
        <div style={{gridColumn:'1/-1'}}>
          <div className="label">어르신 성함</div>
          <input className="input" style={{width:'100%', ...eStyle('elderly_name')}}
                 value={form.elderly_name} onChange={e=>set('elderly_name')(e.target.value)}/>
        </div>
        <div style={{gridColumn:'1/-1'}}>
          <div className="label">어르신 본인 연락처 (선택)</div>
          <PhoneInput style={{width:'100%'}} value={form.elderly_phone} onChange={e=>set('elderly_phone')(e.target.value)}/>
        </div>
      </>)}
      <div>
        <div className="label">나이</div>
        <input className="input" type="number" style={{width:'100%', ...eStyle('age')}}
               value={form.age} onChange={e=>set('age')(e.target.value)} placeholder="세"/>
      </div>
      <div>
        <div className="label">가구형태</div>
        <select className="select" style={{width:'100%', ...eStyle('household_type')}}
                value={form.household_type} onChange={e=>set('household_type')(e.target.value)}>
          <option value="">— 선택 —</option>
          {HOUSEHOLDS.map(h => <option key={h}>{h}</option>)}
        </select>
      </div>
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">주소</div>
        <input className="input" style={{width:'100%', ...eStyle('address')}}
               value={form.address} onChange={e=>set('address')(e.target.value)} placeholder="시/군/구 동 ..."/>
      </div>
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">소속 권역</div>
        {isSystemAdmin ? (
          <select className="select" style={{width:'100%', ...eStyle('region_id')}}
                  value={form.region_id} onChange={e=>set('region_id')(e.target.value)}>
            <option value="">— 선택 —</option>
            {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        ) : (
          // 권역 관리자는 자기 권역으로 고정(서버도 강제) — 선택지를 주지 않는다.
          <div className="input" style={{width:'100%', background:'var(--bg-sub)', color:'var(--ink-2)'}}>
            {regionName || "담당 권역"}
          </div>
        )}
      </div>
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">위치 (선택 — 지정하면 매칭 거리 계산에 사용)</div>
        <LocationPicker
          value={form.latitude != null && form.longitude != null ? { lat: form.latitude, lng: form.longitude } : null}
          center={regionCenter}
          onChange={(c) => setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng }))}
        />
      </div>
      <div style={{gridColumn:'1/-1'}}>
        <div className="label">건강상태 (선택)</div>
        <input className="input" style={{width:'100%'}} value={form.health_status}
               onChange={e=>set('health_status')(e.target.value)} placeholder="예: 고혈압·당뇨, 거동 불편"/>
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:20}}>
      <form className="card" onClick={e=>e.stopPropagation()} onSubmit={submit}
            style={{width:620, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto', padding:24}}>
        {/* 헤더는 sticky — 폼이 길어 스크롤해도 닫기(×)가 항상 손에 닿게 한다.
            음수 마진으로 카드 패딩까지 덮어야 스크롤된 내용이 뒤로 비치지 않는다. */}
        <div style={{position:'sticky', top:0, zIndex:2, background:'var(--surface)',
                     margin:'-24px -24px 16px', padding:'24px 24px 12px',
                     borderBottom:'1px solid var(--border-soft)',
                     display:'flex', alignItems:'flex-start', gap:12}}>
          <div style={{flex:1, minWidth:0}}>
            <div className="h3" style={{marginBottom:4}}>회원 등록</div>
            <div className="muted" style={{fontSize:12, lineHeight:1.6}}>
              운영자가 어르신·보호자 계정을 대신 만듭니다. 등록 즉시 로그인할 수 있고,
              가입 경로와 동일하게 어르신 정보가 함께 생성됩니다.
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="닫기" title="닫기(ESC)"
                  style={{flex:'none', width:30, height:30, display:'grid', placeItems:'center',
                          background:'transparent', border:'none', borderRadius:8, cursor:'pointer',
                          color:'var(--ink-3)', fontSize:20, lineHeight:1, padding:0}}>×</button>
        </div>

        {err && <div role="alert" style={{padding:'10px 12px', marginBottom:12, background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5, lineHeight:1.6}}>{err}</div>}

        {/* 역할 선택 — 이 화면이 다루는 소비자 역할 2종. */}
        <div className="label">역할</div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16}}>
          {CREATE_ROLES.map(r => {
            const on = role === r.v;
            return (
              <button key={r.v} type="button" onClick={()=>{ setRole(r.v); setErrs({}); setErr(null); }}
                      aria-pressed={on}
                      style={{textAlign:'left', padding:'12px 14px', borderRadius:12, cursor:'pointer',
                              border: on ? '1.5px solid var(--orange-500)' : '1px solid var(--border-soft)',
                              background: on ? 'var(--orange-50)' : 'var(--surface)'}}>
                <div style={{fontSize:13, fontWeight:700}}>{r.label}</div>
                <div className="muted" style={{fontSize:11, marginTop:2}}>{r.sub}</div>
              </button>
            );
          })}
        </div>

        {/* 계정 정보 */}
        <div className="label" style={{marginBottom:6}}>계정 정보</div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16}}>
          <div>
            <div className="label">이름</div>
            <input className="input" autoFocus style={{width:'100%', ...eStyle('name')}}
                   value={form.name} onChange={e=>set('name')(e.target.value)}/>
          </div>
          <div>
            <div className="label">연락처 (선택)</div>
            <PhoneInput style={{width:'100%'}} value={form.phone} onChange={e=>set('phone')(e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <div className="label">이메일 (로그인 아이디)</div>
            <input className="input" type="email" style={{width:'100%', ...eStyle('email')}}
                   value={form.email} onChange={e=>set('email')(e.target.value)} placeholder="name@example.com"/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <div className="label">초기 비밀번호 (선택 — 비우면 임시 비밀번호 자동 발급)</div>
            <PasswordInput style={{width:'100%'}} value={form.password} autoComplete="new-password"
                           onChange={e=>set('password')(e.target.value)} placeholder="영문+숫자 조합"/>
          </div>
        </div>

        {/* 역할별 정보 */}
        {role === "guardian" && (
          <div style={{marginBottom:16}}>
            <div className="label" style={{marginBottom:6}}>어르신 연결</div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12}}>
              {[["new","신규 어르신 등록"], ["existing","기존 어르신 연결(연결코드)"]].map(([v, l]) => {
                const on = form.connect_mode === v;
                return (
                  <button key={v} type="button" onClick={()=>{ set('connect_mode')(v); setErrs({}); }}
                          aria-pressed={on}
                          style={{padding:'9px 12px', borderRadius:10, fontSize:12, fontWeight:600, cursor:'pointer',
                                  border: on ? '1.5px solid var(--orange-500)' : '1px solid var(--border-soft)',
                                  background: on ? 'var(--orange-50)' : 'var(--surface)'}}>{l}</button>
                );
              })}
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
              <div>
                <div className="label">어르신과의 관계</div>
                <select className="select" style={{width:'100%', ...eStyle('relation')}}
                        value={form.relation} onChange={e=>set('relation')(e.target.value)}>
                  <option value="">— 선택 —</option>
                  {RELATIONS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              {linkingExisting && (
                <div>
                  <div className="label">연결코드</div>
                  <input className="input" style={{width:'100%', ...eStyle('connect_code')}}
                         value={form.connect_code} onChange={e=>set('connect_code')(e.target.value)}
                         placeholder="어르신에게 발급된 코드"/>
                </div>
              )}
            </div>
          </div>
        )}

        {newElderly && (
          <div style={{marginBottom:16}}>
            <div className="label" style={{marginBottom:6}}>어르신 정보</div>
            {elderlyFields}
          </div>
        )}

        {role === "citizen" && (
          <div style={{marginBottom:16}}>
            <div className="label" style={{marginBottom:6}}>보호자 (비상 연락)</div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
              <div style={{gridColumn:'1/-1'}}>
                <div className="label">보호자 성함</div>
                <input className="input" style={{width:'100%', ...eStyle('guardian_name')}}
                       value={form.guardian_name} onChange={e=>set('guardian_name')(e.target.value)}/>
              </div>
              <div>
                <div className="label">보호자 연락처</div>
                <PhoneInput style={{width:'100%', ...eStyle('guardian_phone')}}
                            value={form.guardian_phone} onChange={e=>set('guardian_phone')(e.target.value)}/>
              </div>
              <div>
                <div className="label">보호자와의 관계</div>
                <select className="select" style={{width:'100%', ...eStyle('relation')}}
                        value={form.relation} onChange={e=>set('relation')(e.target.value)}>
                  <option value="">— 선택 —</option>
                  {RELATIONS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* 등록 옵션 */}
        <div style={{padding:'12px 14px', background:'var(--bg-sub)', borderRadius:10, marginBottom:16, display:'flex', flexDirection:'column', gap:8}}>
          {newElderly && (
            <label style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:12, cursor:'pointer'}}>
              <input type="checkbox" checked={opts.care_request} onChange={e=>setOpts(o=>({...o, care_request:e.target.checked}))}/>
              <span>케어 신청 자동 접수 <span className="muted">— 승인 큐에 올려 바로 케이스로 이어집니다(가입 경로와 동일).</span></span>
            </label>
          )}
          <label style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:12, cursor:'pointer'}}>
            <input type="checkbox" checked={opts.send_invite} onChange={e=>setOpts(o=>({...o, send_invite:e.target.checked}))}/>
            <span>가입 안내 메일 발송 <span className="muted">— 이메일로 접속 정보를 보냅니다.</span></span>
          </label>
          <label style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:12, cursor:'pointer'}}>
            <input type="checkbox" checked={opts.consent} onChange={e=>setOpts(o=>({...o, consent:e.target.checked}))}/>
            <span>
              <b>이용약관·개인정보·민감정보 처리 동의를 본인에게 받았습니다.</b>
              <span className="muted"> (필수 — 동의 이력이 대리 기록되며 등록자가 감사 로그에 남습니다.)</span>
            </span>
          </label>
        </div>

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={busy}
                  style={{flex:1, padding:'11px 0', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:13, fontWeight:600, cursor: busy?'default':'pointer'}}>취소</button>
          <button type="submit" disabled={busy}
                  style={{flex:1, padding:'11px 0', background:'var(--orange-500)', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:700, cursor: busy?'default':'pointer'}}>
            {busy ? '등록 중…' : '회원 등록'}
          </button>
        </div>
      </form>
    </div>
  );
}

// 탈퇴 사유 입력 모달. 사유는 필수(백엔드도 강제) — 감사 로그에 남는다.
function WithdrawModal({ member, onClose, onDone }) {
  const [reason, setReason] = React.useState("");
  const [busy, setBusy]     = React.useState(false);
  const [err, setErr]       = React.useState(null);
  useEscapeKey(onClose);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!reason.trim()) { setErr("탈퇴 사유를 입력해주세요."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/v1/members/${member.id}/withdraw`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(apiErrorMessage(data, r.status));
      }
      onDone();
    } catch (e2) {
      setErr(apiErrorMessage(e2)); setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50}}>
      <form className="card" onClick={e=>e.stopPropagation()} onSubmit={submit}
            style={{width:420, maxWidth:'90vw', padding:22}}>
        <div className="h3" style={{marginBottom:6}}>회원 탈퇴 처리</div>
        <div className="muted" style={{fontSize:12.5, lineHeight:1.7, marginBottom:14}}>
          <b>{member.name}</b> ({member.email}) 님을 탈퇴 처리합니다. 즉시 로그인이 차단되며,
          유예기간이 지나면 개인정보가 자동 익명화됩니다. 유예기간 내에는 재활성화로 복구할 수 있습니다.
        </div>
        {err && <div role="alert" style={{padding:'10px 12px', marginBottom:10, background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}
        <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>탈퇴 사유 (필수)</label>
        <input className="input" autoFocus style={{width:'100%', marginTop:4}}
               value={reason} onChange={e=>setReason(e.target.value)} placeholder="예) 본인 요청 · 중복 가입 정리"/>
        <div style={{display:'flex', gap:8, marginTop:18}}>
          <button type="button" onClick={onClose} disabled={busy}
                  style={{flex:1, padding:'10px 0', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:13, fontWeight:600, cursor: busy?'default':'pointer'}}>취소</button>
          <button type="submit" disabled={busy}
                  style={{flex:1, padding:'10px 0', background:'var(--rose-500)', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:700, cursor: busy?'default':'pointer'}}>
            {busy ? '처리 중…' : '탈퇴 처리'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Members() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  const [members, setMembers] = React.useState([]);
  const [counts, setCounts]   = React.useState({ total: 0, active: 0, withdrawn: 0 });
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 0 });
  const [q, setQ]         = React.useState("");
  const [role, setRole]   = React.useState("all");
  const [state, setState] = React.useState("all");
  const [page, setPage]   = React.useState(1);
  const [withdrawing, setWithdrawing] = React.useState(null); // member 객체
  const [creating, setCreating] = React.useState(false);
  // 등록 결과 배너 — 자동 발급된 임시 비밀번호는 여기서만 한 번 볼 수 있다.
  const [created, setCreated] = React.useState(null);
  const [accessError, setAccessError] = React.useState(null);

  // 검색·필터 변경 시 1페이지로.
  React.useEffect(() => { setPage(1); }, [q, role, state]);

  const load = React.useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE) });
    if (q.trim()) params.set("q", q.trim());
    if (role !== "all")  params.set("role", role);
    if (state !== "all") params.set("state", state);
    try {
      const r = await fetch(`/api/v1/members?${params.toString()}`, { credentials: "same-origin" });
      const gate = await authGateMessage(r);
      if (gate) { setAccessError(gate); setMembers([]); return; }
      setAccessError(null);
      const data = await r.json();
      setMembers(data.members || []);
      setCounts(data.counts || { total: 0, active: 0, withdrawn: 0 });
      setPagination(data.pagination || { page: 1, total: 0, total_pages: 0 });
    } catch (e) {
      setAccessError("회원 목록을 불러오지 못했습니다.");
    }
  }, [page, q, role, state]);

  React.useEffect(() => { load(); }, [load]);

  const reactivate = async (m) => {
    if (!window.confirm(`${m.name} 님의 탈퇴를 철회하고 계정을 다시 활성화할까요?`)) return;
    try {
      const r = await fetch(`/api/v1/members/${m.id}/reactivate`, {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        alert(apiErrorMessage(data, r.status));
        return;
      }
      load();
    } catch (e) { alert("재활성화에 실패했습니다."); }
  };

  const navRole = user?.role === "system_admin" ? "platform" : "region";

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role={navRole} active="members"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="회원 관리" sub="어르신·보호자 회원 계정과 탈퇴 처리"/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18, gap:14}}>
              <div>
                <div className="h1">회원 계정</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>어르신·보호자 회원을 등록·조회하고, 탈퇴 처리 및 유예기간 내 복구를 관리합니다.</div>
              </div>
              {perms.canManageMembers && !accessError && (
                <button type="button" onClick={()=>setCreating(true)}
                        style={{display:'inline-flex', alignItems:'center', gap:7, padding:'10px 16px', background:'var(--orange-500)', color:'#fff',
                                border:'none', borderRadius:10, fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                  <span style={{display:'inline-flex', width:15, height:15}}>{Ic.plus}</span>
                  회원 등록
                </button>
              )}
            </div>

            {/* 등록 직후 안내 — 자동 발급 임시 비밀번호는 이 배너에서만 확인할 수 있다. */}
            {created && (
              <div style={{padding:'14px 18px', background:'var(--green-50)', border:'1px solid var(--green-300)',
                           borderRadius:12, marginBottom:14, display:'flex', alignItems:'flex-start', gap:12}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:700, color:'var(--green-600)'}}>
                    {created.name} ({created.role_label}) 회원이 등록되었습니다.
                  </div>
                  <div className="muted" style={{fontSize:12, marginTop:5, lineHeight:1.7}}>
                    이메일 <b className="mono">{created.email}</b>
                    {created.temp_password && <> · 임시 비밀번호 <b className="mono" style={{background:'var(--surface)', padding:'2px 6px', borderRadius:5}}>{created.temp_password}</b> <span style={{color:'var(--rose-500)'}}>(이 화면을 벗어나면 다시 볼 수 없습니다)</span></>}
                    <br/>
                    {created.elderly?.name && <>연결 어르신 <b>{created.elderly.name}</b> · </>}
                    {created.care_request ? "케어 신청이 승인 큐에 접수되었습니다." : "케어 신청은 접수하지 않았습니다."}
                    {created.invited ? " · 안내 메일 발송" : " · 안내 메일 미발송"}
                  </div>
                </div>
                <button type="button" onClick={()=>setCreated(null)} aria-label="닫기"
                        style={{background:'transparent', border:'none', cursor:'pointer', color:'var(--ink-3)', fontSize:16, lineHeight:1, padding:4}}>×</button>
              </div>
            )}

            {accessError && (
              <div style={{padding:'12px 16px', background:'var(--rose-50)', color:'var(--rose-500)',
                           borderRadius:10, marginBottom:14, fontSize:13, display:'flex', alignItems:'center', gap:10}}>
                <span style={{fontWeight:700}}>접근 불가</span>
                <span>{accessError}</span>
                <a href="/login" className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}}>로그인</a>
              </div>
            )}

            <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="회원 총원" value={`${counts.total.toLocaleString()}명`}/>
              <Kpi label="활성 회원" value={`${counts.active.toLocaleString()}명`} color="var(--green-500)"/>
              <Kpi label="탈퇴 회원" value={`${counts.withdrawn.toLocaleString()}명`} color="var(--ink-3)"/>
            </div>

            <div className="card" style={{overflow:'hidden'}}>
              <div style={{padding:'14px 22px', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid var(--border-soft)'}}>
                <div className="searchbar" style={{flex:'0 0 260px'}}>{Ic.search}<input className="input" placeholder="이름·이메일·연락처" value={q} onChange={e=>setQ(e.target.value)}/></div>
                <select className="select" value={role} onChange={e=>setRole(e.target.value)} style={{width:140}}>
                  <option value="all">전체 역할</option>
                  <option value="citizen">어르신</option>
                  <option value="guardian">보호자</option>
                </select>
                <select className="select" value={state} onChange={e=>setState(e.target.value)} style={{width:120}}>
                  <option value="all">전체 상태</option>
                  <option value="active">활성</option>
                  {/* 대리 등록 직후(첫 로그인 전) 상태 — 등록한 회원을 바로 찾을 수 있게. */}
                  <option value="invited">초대</option>
                  <option value="off">탈퇴</option>
                </select>
              </div>
              <table className="tbl">
                <thead><tr>
                  <th style={{paddingLeft:22}}>회원</th>
                  <th>역할</th>
                  <th>연락처</th>
                  <th>담당 권역</th>
                  <th>연결 어르신</th>
                  <th>상태</th>
                  <th>탈퇴일</th>
                  <th style={{textAlign:'right', paddingRight:22}}>작업</th>
                </tr></thead>
                <tbody>
                  {members.map(m => {
                    const rb = ROLE_LABEL[m.role] || { label: m.role, color: "gray" };
                    // 단일 소스: 탈퇴 여부는 withdrawn(=withdrawn_at) 하나로 판단한다.
                    // 작업 버튼은 백엔드 가드(withdraw/reactivate가 withdrawn?로 검사)와
                    // 일치해야 하므로 withdrawn 기준일 수밖에 없고, 뱃지의 "탈퇴" 판정도
                    // 같은 값에서 도출해 뱃지·버튼이 절대 어긋나지 않게 한다.
                    // (state="off"+미탈퇴 같은 이상 데이터에도 활성으로 표시 → 버튼과 일관.)
                    const isWithdrawn = m.withdrawn;
                    const sb = isWithdrawn
                      ? STATE_LABEL.off
                      : (STATE_LABEL[m.state] && m.state !== "off")
                          ? STATE_LABEL[m.state]
                          : STATE_LABEL.active;
                    const menu = [
                      perms.canManageMembers && !isWithdrawn && {
                        label: "탈퇴 처리", danger: true, onClick: () => setWithdrawing(m),
                        title: "회원을 탈퇴 처리 (로그인 차단)" },
                      perms.canManageMembers && isWithdrawn && !m.anonymized && {
                        label: "재활성", color: 'var(--green-600)', onClick: () => reactivate(m),
                        title: "탈퇴 철회 + 계정 재활성화" },
                    ];
                    return (
                      <tr key={m.id}>
                        <td style={{paddingLeft:22}}>
                          <div style={{display:'flex', alignItems:'center', gap:10}}>
                            <div className="avatar sm" style={{background:'linear-gradient(135deg,#7CC0E8,#5B9BD8)', overflow:'hidden'}}>
                              {m.avatar_url
                                ? <img src={m.avatar_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
                                : (m.name||'·')[0]}
                            </div>
                            <div>
                              <div style={{fontWeight:700, fontSize:12.5}}>{m.name}</div>
                              <div className="muted" style={{fontSize:10.5}}>{m.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={"badge badge-" + rb.color}>{rb.label}</span></td>
                        <td className="mono muted">{m.phone || "—"}</td>
                        <td className="muted">{m.region}</td>
                        <td className="muted">{m.elderly_name || "—"}</td>
                        <td>
                          <span className={"badge badge-" + sb.color}>{sb.label}</span>
                          {m.anonymized && (
                            <div className="muted" style={{fontSize:10, marginTop:3}} title={`익명화: ${fullTimestamp(m.anonymized_at)}`}>익명화 완료</div>
                          )}
                        </td>
                        <td className="mono muted">
                          <span title={m.withdraw_reason ? `사유: ${m.withdraw_reason}` : ''}>{withdrawnLabel(m.withdrawn_at)}</span>
                        </td>
                        <td style={{textAlign:'right', paddingRight:22}}>
                          {menu.some(Boolean) ? <RowMenu items={menu} /> : <span className="muted" style={{fontSize:11}}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {members.length === 0 && !accessError && (
                    <tr><td colSpan={8} style={{textAlign:'center', padding:'36px 0', color:'var(--ink-3)', fontSize:13}}>표시할 회원이 없습니다.</td></tr>
                  )}
                </tbody>
              </table>

              {pagination.total > 0 && (
                <div style={{padding:'10px 22px', borderTop:'1px solid var(--border-soft)',
                             display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <span className="muted" style={{fontSize:11.5}}>
                    페이지 <b>{pagination.page}</b> / {pagination.total_pages} ·
                    전체 <b>{pagination.total}</b>명 (페이지당 {PER_PAGE}명)
                  </span>
                  <Pager page={pagination.page} totalPages={pagination.total_pages} onPage={setPage}/>
                </div>
              )}
            </div>

            <AppFooter/>
          </div>
        </div>
      </div>
      {withdrawing && (
        <WithdrawModal member={withdrawing}
          onClose={() => setWithdrawing(null)}
          onDone={() => { setWithdrawing(null); load(); }}/>
      )}
      {creating && (
        <MemberCreateModal user={user}
          onClose={() => setCreating(false)}
          onDone={(data) => {
            setCreating(false);
            setCreated(data);
            // 새 회원이 첫 페이지에 보이도록 필터를 초기 상태로 되돌린 뒤 재조회.
            setQ(""); setRole("all"); setState("all"); setPage(1);
            load();
            // 승인 대기(케어 신청) 배지 등 사이드바 카운트 즉시 갱신.
            window.dispatchEvent(new Event("session:refresh"));
          }}/>
      )}
    </div>
  );
}

export { Members };
